import { useEffect, useMemo, useState } from 'react';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import { sortRows } from '@/utils/tableSort';
import type {
  AnySourceConfig, FilterValues, ReportColumn, Row, RowTint, ServerSort, SourceKey,
} from '../types';
import { formatCell, rowMatches } from '../utils';
import { useSourceData } from './useSourceData';

interface Args {
  sourceKey: SourceKey;
  source: AnySourceConfig;
  applied: FilterValues;
  selectedColumns: ReportColumn[];
  search: string;
  /**
   * Fully enriched rows (Request export after the missing-data run). When set, the table works on
   * these in the browser — client-side sort and paging — instead of asking the server for a page.
   */
  overrideRows?: Row[] | null;
}

const SORT_KEY_BY_COLUMN: Record<string, Record<string, string>> = {
  intake: { fullName: 'fullName', courseName: 'courseName', startDisplay: 'startDateTime', submittedDateTime: 'submittedDateTime', statusName: 'statusName' },
  request: { fullName: 'fullName', needName: 'needName', locationName: 'locationName', submittedDateTime: 'submittedDateTime', statusName: 'statusName' },
};

/**
 * Assigns 'a'/'b' alternately to grouped rows within a page. A non-grouped row resets the
 * alternation, so two adjacent groups are always different colours. Computed per page.
 */
function buildTintMap(
  pageRows: Row[],
  primaryKey: string,
  groupField: string | undefined,
  groupThreshold = 1,
): Map<string, RowTint> {
  const map = new Map<string, RowTint>();
  if (!groupField) return map;
  let idx = 0;
  for (const row of pageRows) {
    const id = String(row[primaryKey] ?? '');
    const count = Number(row[groupField] ?? 0);
    if (count > groupThreshold) {
      map.set(id, idx % 2 === 0 ? 'a' : 'b');
      idx += 1;
    } else {
      idx = 0; // default rows break the run
    }
  }
  return map;
}

/** Plain cell value, shared by the table, the CSV and the client-side sort. */
const accessorFor = (c: ReportColumn) => (row: Row) => {
  if (c.key === 'fullName') {
    const full = row.fullName as string | undefined;
    if (full && full.trim()) return full;
    return [row.firstName, row.surname].filter(Boolean).join(' ');
  }
  return formatCell(row[c.key], c.type);
};

export function useReportTable({
  sourceKey, source, applied, selectedColumns, search, overrideRows = null,
}: Args) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [serverSort, setServerSort] = useState<ServerSort | null>(null);
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const enriched = overrideRows !== null;
  // ⚠️ Enriched rows exist only in the browser, so the server can no longer sort or page them.
  const isPaged = source.mode === 'paged' && !enriched;

  const data = useSourceData({
    sourceKey, source, applied,
    sort: isPaged ? serverSort : null,
    search,
    page: activePage,
    pageSize,
  });

  // The rows a client-side table works on: enriched rows, or everything a streamed source loaded.
  const clientBase: Row[] | null = enriched
    ? overrideRows
    : source.mode === 'streamed' ? data.rows : null;

  // Streamed sources are filtered here; enriched rows were already filtered by the server.
  const clientFiltered = useMemo(() => {
    if (!clientBase) return [];
    if (enriched) return clientBase;
    return clientBase.filter((r) => rowMatches(r, source.filters, applied));
  }, [clientBase, enriched, source, applied]);

  // ⚠️ sortRows finds the clicked column by key and sorts by ITS accessor. It used to be handed
  // `[]`, so it could never find the column and streamed sources silently never sorted.
  // These render-free columns exist only to break the cycle with the tint-aware table columns.
  const sortColumns = useMemo<TableColumn<Row>[]>(
    () => selectedColumns.map((c) => ({
      key: c.key, header: c.label, sortable: true, accessor: accessorFor(c),
    })),
    [selectedColumns],
  );

  const sortedClientRows = useMemo(() => {
    if (!clientBase) return data.rows;
    return sortRows(clientFiltered, sortColumns, sortConfig);
  }, [clientBase, data.rows, clientFiltered, sortColumns, sortConfig]);

  // ── Paging ──
  const totalItems = isPaged ? data.totalCount : sortedClientRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => { setActivePage(1); }, [applied, pageSize, sourceKey, search, enriched]);
  useEffect(() => { if (activePage > totalPages) setActivePage(totalPages); }, [activePage, totalPages]);

  const paginatedRows = useMemo(() => {
    if (isPaged) return data.rows;
    const first = (activePage - 1) * pageSize;
    return sortedClientRows.slice(first, first + pageSize);
  }, [isPaged, data.rows, sortedClientRows, activePage, pageSize]);

  // ── Row tint map for the current page (streamed sources with a group field) ──
  const tintMap = useMemo(() => {
    if (source.mode !== 'streamed' || !source.groupField) return new Map<string, RowTint>();
    return buildTintMap(paginatedRows, source.primaryKey, source.groupField);
  }, [source, paginatedRows]);

  // ── Table columns: same defs drive the preview and the CSV ──
  const tableColumns = useMemo<TableColumn<Row>[]>(
    () =>
      selectedColumns.map((c) => ({
        key: c.key,
        header: c.label,
        sortable: isPaged
          ? Boolean(SORT_KEY_BY_COLUMN[sourceKey]?.[c.key])
          : true,
        accessor: accessorFor(c),
        // Only streamed+grouped sources use `render`; paged sources pass the column through as-is.
        render: c.render && source.mode === 'streamed'
          ? (value: unknown, row: Row) => {
              const id = String(row[source.primaryKey] ?? '');
              const tint = tintMap.get(id);
              return c.render!(value, row, { tint });
            }
          : undefined,
      })),
    [selectedColumns, isPaged, sourceKey, source, tintMap],
  );

  const handleSort = (config: SortConfig | null) => {
    if (!isPaged) {
      setSortConfig(config);
      setActivePage(1);
      return;
    }
    if (!config) {
      setServerSort(null);
      setActivePage(1);
      return;
    }
    const sortBy = SORT_KEY_BY_COLUMN[sourceKey]?.[config.field];
    if (!sortBy) return;
    setServerSort({ sortBy, sortDir: config.direction === 'desc' ? 'desc' : 'asc' });
    setActivePage(1);
  };

  return {
    tableColumns,
    paginatedRows,
    sortedRows: isPaged ? data.rows : sortedClientRows,
    totalItems,
    // Paged: the server's filtered total. Streamed / enriched: everything loaded, so
    // "X of Y match" compares the filtered count against the unfiltered one.
    serverTotalCount: isPaged ? data.totalCount : (clientBase ? clientBase.length : data.rows.length),
    activePage,
    pageSize,
    setActivePage,
    setPageSize,
    loading: data.loading,
    error: data.error,
    stream: data.stream,
    fetchPage: data.fetchPage,
    handleSort,
    resetSort: () => { setSortConfig(null); setServerSort(null); },
  };
}
